const networkErrorResponse = {
  success: false,
  message: "Network error. Please refresh the page and try again."
};

// There is no error-reporting backend wired up (no SENTRY_DSN is fetched, and
// Sentry was never initialised), so failures go to the console.
const reportError = (err: unknown, where: string, context?: Record<string, unknown>) => {
  // eslint-disable-next-line no-console
  console.error(`[requests] ${where}`, err, context ?? {});
};

const getCsrfToken = (): string => {
  const meta = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null;
  return meta?.content ?? "";
};

const jsonHeaders = (): Record<string, string> => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  "X-CSRF-Token": getCsrfToken()
});

// Generic: send arbitrary JSON to a Rails controller action.
export const sendToRailsController = async (data: object, url: string): Promise<any> => {
  try {
    const response = await fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: jsonHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      reportError(`Non-OK response (${response.status})`, "sendToRailsController", { url });
      return { ...networkErrorResponse, message: `Server error (${response.status}).` };
    }
    return await response.json();
  } catch (err: any) {
    reportError(err, "sendToRailsController", {
      url,
      data: JSON.stringify(data).substring(0, 500)
    });
    return networkErrorResponse;
  }
};

// "Directive + data" helper: posts { directive, data } to a generic endpoint.
export const sendDirectiveAndDataToRailsController = async (
  directive: string,
  data: object,
  url: string
): Promise<any> => {
  return sendToRailsController({ directive, data }, url);
};

// Helper specifically for GET requests that return JSON.
export const getJsonFromRails = async (url: string): Promise<any> => {
  try {
    const response = await fetch(url, {
      method: "GET",
      credentials: "same-origin",
      headers: { Accept: "application/json" }
    });
    if (!response.ok) return { ...networkErrorResponse, message: `Server error (${response.status}).` };
    return await response.json();
  } catch (err: any) {
    reportError(err, "getJsonFromRails", { url });
    return networkErrorResponse;
  }
};

export const deleteFromRailsController = async (url: string): Promise<any> => {
  try {
    const response = await fetch(url, {
      method: "DELETE",
      credentials: "same-origin",
      headers: jsonHeaders()
    });
    if (!response.ok) {
      return { success: false, message: `Server error (${response.status}).` };
    }
    return await response.json();
  } catch (err: any) {
    reportError(err, "deleteFromRailsController", { url });
    return networkErrorResponse;
  }
};
